import type { IApplicationInsights } from '@microsoft/applicationinsights-web'
import type { NuxtApp } from '#app'
import {Culture, LanguagePath} from '~/types/locale'
import {useLocaleStore} from '~/stores/locale'
import {Env} from '~/types/environment'
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'
import { getRequestHost } from '~/utils/locale'

// An IPv4 address, with or without a port (e.g. a pod's `10.97.17.164:3000`).
const IP_HOST = /^\d{1,3}(\.\d{1,3}){3}(:\d+)?$/

// The plugin body, exported so a spec can run it: the nuxt test environment
// boots every plugin and has no i18n-micro locale runtime, so the plugin itself
// still stands down under VITEST.
export const syncLocale = async (nuxtApp: NuxtApp) => {
    const { ssrContext } = nuxtApp
    const $appInsights: Pick<IApplicationInsights, 'trackEvent'> | undefined = nuxtApp.$appInsights
    const $i18n = useI18n()
    const defaultCulture = $i18n.defaultLocale() as Culture
    const config = useTypedRuntimeConfig()
    const env = config.public.ENVIRONMENT

    // env to know redirection
    const localeStore = useLocaleStore()
    localeStore.env = env ?? Env.Release

    // SSR requests only: the locale comes from the request's host header.
    if (ssrContext?.event.node.req) {
        const req = ssrContext?.event.node.req
        const hostHeader = getRequestHost(req.headers)
        const rawUrl: string = (req.headers.sturl as string | undefined) ?? req.url ?? ''
        const urlSegments = rawUrl.split('/')
        const secondSegment = urlSegments[1]
        const hasLang = secondSegment !== undefined && /^[^?].($|\?)/.test(secondSegment)
        const lang: string | undefined = hasLang ? secondSegment.slice(0, 2) : undefined

        if (lang && !(Object.values(LanguagePath) as string[]).includes(`/${lang}`)) {
            $appInsights?.trackEvent({name: 'possible invalid lang in url', properties: {lang}})
        }
        const stampinupDomain = localeStore.getLocaleFromHeaders(hostHeader, lang)
        // A host outside the stampinup domains falls back to the default
        // culture; record it so a misrouted proxy or domain shows up. Local dev
        // always runs on localhost, so it would fire on every render there.
        // In-cluster requests to a pod's own IP are not a misroute and would
        // bury the signal: nuxt2's version of this event was 99.7% pod-IP
        // hosts in PROD (416,889 of 418,174 over 7 days, 2026-10-14).
        if (env !== Env.Local && hostHeader && !stampinupDomain && !IP_HOST.test(hostHeader)) {
            $appInsights?.trackEvent({name: 'non stampinup domain', properties: {host: hostHeader}})
        }
    }

    if (!localeStore.isLocaleSet) {
        localeStore.fallbackLocale(defaultCulture)
    }

    localeStore.updateBaseUrl(localeStore.culture ?? Culture.EN_US)

    useI18nLocale().setLocale(localeStore.culture)
}

export default defineNuxtPlugin({
    name: 'locale-persistent-sync',
    dependsOn: ['services'],
    async setup(nuxtApp) {
        if (process.env.VITEST) {
            return
        }
        await syncLocale(nuxtApp)
    }
})
